/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { InterPoint, useLocalize } from '@saviynt/common';
import {
  ApplicationIcon,
  Box,
  Button,
  Checkbox,
  FooterActionBar,
  Icon,
  InlineMessage,
  Link as DsLink,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { removeUnSelectedRoles } from '../../store/actions/bulkRoleSelectAction';

import { msgs } from './contants';

import './ManageItemsModal.css';

const CheckboxItem = React.memo(({ item, index, onChange }) => {
  const handleChange = useCallback(
    (event) => onChange(index, event.target.checked),
    [index, onChange]
  );

  const renderTitle = (
    <Box className='ManageItems-listTitleContainer'>
      <Typography kind='h3' lineLimit={2}>
        {item?.roleDisplayName || item?.roleName}
      </Typography>

      {item?.platformType && (
        <>
          <InterPoint />
          <ApplicationIcon size='xxSmall' logo={item.platformType} />
          <Typography
            kind='body3'
            className='ManageItems-listTitleContainer--platformType'>
            {item?.platformType || ''}
          </Typography>
        </>
      )}
    </Box>
  );

  const description = (
    <Typography
      kind='body2'
      lineLimit={5}
      color='--color-foreground-secondary-bold'>
      {item?.roleDescription || ''}
    </Typography>
  );

  return (
    <Checkbox
      key={`${item.roleName}-${index}`}
      kind='multiLine'
      className='ManageItems-checkbox'
      label={renderTitle}
      description={description}
      descriptionLineLimit={3}
      showHoverEffect
      isChecked={item.isChecked}
      onChange={handleChange}
    />
  );
});

CheckboxItem.propTypes = {
  item: PropTypes.shape({
    roleName: PropTypes.string,
    roleDisplayName: PropTypes.string,
    roleDescription: PropTypes.string,
    platformType: PropTypes.string,
    isChecked: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

const ModalSubtitle = React.memo(({ isJitaEnabledAccount }) => {
  const history = useHistory();
  const localize = useLocalize();
  const handleNavigate = useCallback(() => {
    history.push(
      isJitaEnabledAccount ? '/request/jitAccess' : '/request/privilegedAccess'
    );
  }, [history, isJitaEnabledAccount]);

  return (
    <Box>
      <Typography kind='body2' color='--color-foreground-neutral-medium'>
        {localize(msgs.pamManageItemsModal?.modalSubTitle)}
      </Typography>
      <div className='ManageItems-selectionContainer'>
        <InlineMessage
          text={localize(msgs.pamManageItemsModal?.modalSubTitleInlineMsg)}
          colorTheme='info'
          size='small'
          leftIcon={<Icon kind='AlertInfoFilled' />}
        />
        <DsLink
          kind='button'
          color='brand'
          dataTheme='light'
          onClick={handleNavigate}
          text={
            <Typography
              kind='body3'
              className='ManageItems-selectionLink'
              color='--color-foreground-primary-medium'>
              {localize(msgs.pamManageItemsModal?.modalSelectionPage)}
            </Typography>
          }
          size='small'
          suffixIconKind='ArrowRight'
        />
      </div>
    </Box>
  );
});

function ManageItemsModal({
  isModalOpen,
  selectedRoles,
  setIsModalOpen,
  isJitaEnabledAccount,
}) {
  const dispatch = useDispatch();
  const localize = useLocalize();
  const [selectedItems, setSelectedItems] = useState([]);
  const [initialItems, setInitialItems] = useState([]);

  useEffect(() => {
    if (isModalOpen && selectedRoles.length > 0) {
      const updatedItems = selectedRoles.map((item) => ({
        ...item,
        isChecked: true,
      }));

      setSelectedItems(updatedItems);
      setInitialItems(updatedItems);
    }

    return () => {
      setSelectedItems([]);
      setInitialItems([]);
    };
  }, [selectedRoles, isModalOpen]);

  const isSaveDisabled = useMemo(
    () => selectedItems.every((item) => !item.isChecked),
    [selectedItems]
  );

  const hasNoChanges = useMemo(
    () => JSON.stringify(selectedItems) === JSON.stringify(initialItems),
    [selectedItems, initialItems]
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const handleSave = useCallback(() => {
    dispatch(removeUnSelectedRoles(selectedItems));
    setIsModalOpen(false);
  }, [dispatch, selectedItems, setIsModalOpen]);

  const handleCheckboxChange = useCallback((index, checked) => {
    setSelectedItems((prevItems) =>
      prevItems.map((item, idx) =>
        idx === index ? { ...item, isChecked: checked } : item
      )
    );
  }, []);

  const renderModalBody = useCallback(
    () => (
      <div className='ManageItems-content'>
        {selectedItems.map((item, index) => (
          <CheckboxItem
            key={`${item.roleName}-${index}`}
            item={item}
            index={index}
            onChange={handleCheckboxChange}
          />
        ))}
      </div>
    ),
    [selectedItems, handleCheckboxChange]
  );

  const renderModalFooter = useCallback(
    () => (
      <FooterActionBar className='ManageItems-footer'>
        {isSaveDisabled && isModalOpen && (
          <InlineMessage
            className='ManageItems-footer-inlineMsg'
            colorTheme='warning'
            size='medium'
            isVisible
            leftIcon={<Icon kind='AlertWarningFilled' />}
            text={localize(msgs.pamManageItemsModal?.modalFooterMsg)}
          />
        )}
        <div className='ManageItems-footer-buttons'>
          <Button
            type='button'
            kind='ghost'
            size='medium'
            onClick={handleCloseModal}>
            {localize(msgs.pamManageItemsModal.modalCancel)}
          </Button>
          <Button
            type='button'
            kind='filled'
            size='medium'
            isDisabled={isSaveDisabled || hasNoChanges}
            onClick={handleSave}>
            {localize(msgs.pamManageItemsModal.modalSave)}
          </Button>
        </div>
      </FooterActionBar>
    ),
    [isSaveDisabled, hasNoChanges, handleSave, localize]
  );

  return (
    <ModalIsland
      className='ManageItems'
      onClose={handleCloseModal}
      isOpen={isModalOpen}
      title={
        <Typography kind='h1'>
          {localize(msgs.pamManageItemsModal.modalHeader)}
        </Typography>
      }
      subtitle={<ModalSubtitle isJitaEnabledAccount={isJitaEnabledAccount} />}
      headerIcon={
        <Icon
          kind='RequestAccess'
          size='large'
          color='--color-foreground-neutral-bold'
        />
      }
      contentBody={renderModalBody()}
      FooterActionBarComp={renderModalFooter()}
    />
  );
}

ManageItemsModal.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  setIsModalOpen: PropTypes.func.isRequired,
  selectedRoles: PropTypes.arrayOf(
    PropTypes.shape({
      roleName: PropTypes.string,
      roleDisplayName: PropTypes.string,
      roleDescription: PropTypes.string,
      platformType: PropTypes.string,
    })
  ).isRequired,
};

export default React.memo(ManageItemsModal);
